import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from "react";
import { usePricemanTablePageSize } from "../common/usePricemanTablePageSize";
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { toast } from "react-toastify";
import { Dialog } from 'primereact/dialog';
import PPUtils from "../../utils/PPUtils"
import Select from "react-select"
import RuleEngineBackend from "../services/RuleEngineBackend"
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import PPColumnDateFilter from "../common/PPColumnDateFilter";
import { getPrimeCalendarConfig } from "../common/primeCalendarSetup";
import { isDateFilterField } from "../common/tableQueryUtils";
import { resolveChannelId, selectChannel } from "../common/priceManagementChannelStorage";
import PPDeleteConfirmationModal from "../editor/modals/PPDeleteConfirmationModal";
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner"
import "../../styles/priceman.css"
import { persistUserSettingsLocalstorage } from "../../utils/userSettingsLocalstorage";
import { loadStoredJsonObject, saveStoredJson, mergeTableFilters } from "../common/tableStateStorage";

const RULES_FILTERS_STORAGE = "rulestable-state-local";

function channelsForSelect(allChannels) {
     if (!allChannels) {
          return null;
     }
     const channelList = Array.isArray(allChannels) ? allChannels : [allChannels];
     const visible = channelList.filter((c) => !c.hidden);
     return { channelList, visible: visible.length > 0 ? visible : channelList };
}

function buildEmptyFilters() {
     return {
          'id': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'rulePriority': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'enabled': { value: null, matchMode: FilterMatchMode.EQUALS },
          'name': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'description': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'createDateFormatted': { value: null, matchMode: FilterMatchMode.DATE_IS },
          'ruleGroup.groupName': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'creatorUser.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
          'lastEditUser.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
     };
}

function loadStoredFilters() {
     const parsed = loadStoredJsonObject(RULES_FILTERS_STORAGE);
     return mergeTableFilters(buildEmptyFilters(), parsed?.filters);
}


export default function PPPriceManagementRules() {

     const [channels, setChannels] = useState([]);
     const [selectedChannelID, setSelectedChannelID] = useState(null);
     const [tableData, setTableData] = useState([]);
     const [searchKeyword, setSearchKeyword] = useState("");
     const [activeSearchKeyword, setActiveSearchKeyword] = useState("");
     const [stagingModeActive, setStagingModeActive] = useState(false);
     const [isLoading, setIsLoading] = useState(false);
     const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
     const [ruleDeleteModalVisible, setRuleDeleteModalVisible] = useState(false);
     const [rulePendingDelete, setRulePendingDelete] = useState(null);
     const [reloadDialogOpen, setReloadDialogOpen] = useState(false);
     const [stagingDialogOpen, setStagingDialogOpen] = useState(false);
     const [filters, setFilters] = useState(loadStoredFilters);
     const [multiSortMeta, setMultiSortMeta] = useState([]);

     const { t, i18n } = useTranslation();
     const navigate = useNavigate();
     const pendingSearchToast = useRef(false);
     const tableWrapRef = useRef(null);
     const pageSize = usePricemanTablePageSize(tableWrapRef, { defaultRows: 12 });

     const filterTranslationMap = {
          id: t("pricemanagement_page.overview_page.table_header_id"),
          rulePriority: t("pricemanagement_page.overview_page.table_header_priority"),
          enabled: t("pricemanagement_page.overview_page.table_header_active"),
          name: t("pricemanagement_page.overview_page.table_header_name"),
          description: t("pricemanagement_page.overview_page.table_header_description"),
          createDate: t("pricemanagement_page.overview_page.table_header_creation_date"),
          'ruleGroup.groupName': t("pricemanagement_page.overview_page.table_header_group"),
          'creatorUser.name': t("pricemanagement_page.overview_page.table_header_author"),
          'lastEditUser.name': t("pricemanagement_page.overview_page.table_header_last_edit")
     }

     let auth = PPUtils.getLogin();
     const userId = auth.user?.id;
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     useEffect(() => {
          saveStoredJson(RULES_FILTERS_STORAGE, { filters });
     }, [filters]);

     async function reloadAllPrices() {
          const response = await RuleEngineBackend.reloadAllPrices(customerId, token);
          if (response?.ok) {
               window.location.reload();
          } else {
               toast.error(t("pricemanagement_page.overview_page.toast_error"));
          }
     }

     async function deleteAllPrices() {
          const response = await RuleEngineBackend.deleteAllPrices(customerId, token);
          if (response?.ok) {
               toast.success("Preise wurden gelöscht");
          } else {
               toast.error(t("pricemanagement_page.overview_page.toast_error"));
          }
     }

     async function deactivateStagingMode() {
          const response = await RuleEngineBackend.setStagingModeActive(customerId, token, false);
          if (response.ok) {
               setStagingModeActive(false);
               toast.success(t("pricemanagement_page.overview_page.staging_mode") + ": Off");
          } else {
               toast.error(t("pricemanagement_page.overview_page.toast_error"));
          }
     }

     async function activateStagingMode() {
          const response = await RuleEngineBackend.setStagingModeActive(customerId, token, true);
          if (response.ok) {
               setStagingModeActive(true);
               toast.success(t("pricemanagement_page.overview_page.staging_mode") + ": ON");
          } else {
               toast.error(t("pricemanagement_page.overview_page.toast_error"));
          }
     }

     async function openRuleEdit(event) {
          const ruleId = event.data.id;
          navigate(`/pricemanagement/editor/${ruleId}`);
     }

     function resetAllFilters() {
          const resetFilters = buildEmptyFilters();
          setFilters(resetFilters);
          setMultiSortMeta([]);
          setSearchKeyword("");
          setActiveSearchKeyword("");
          pendingSearchToast.current = false;
          saveStoredJson(RULES_FILTERS_STORAGE, { filters: resetFilters });
          persistUserSettingsLocalstorage();
     }

     function mapRulesForTable(rules) {
          return rules.map(rule => ({
               ...rule,
               createDateFormatted: new Date(rule.createDate).toLocaleString('de-DE'),
               createDate: new Date(rule.createDate)
          }));
     }

     function sortRules(rules) {
          return [...rules].sort((a, b) => {
               const ca = (a.channel?.channelName || "").localeCompare(b.channel?.channelName || "");
               if (ca !== 0) return ca;
               return (a.rulePriority || 0) - (b.rulePriority || 0);
          });
     }

     async function loadOverviewRules(channelId) {
          let allRules = await RuleEngineBackend.getAllRules(customerId, token);
          if (!allRules) {
               setTableData([]);
               return;
          }
          const channelFiltered = channelId == null || channelId < 0
               ? allRules
               : allRules.filter((row) => row.channel && Number(row.channel.id) === Number(channelId));
          setTableData(mapRulesForTable(channelFiltered));
     }

     async function loadKeywordSearchRules(keyword, channelId) {
          const rules = await RuleEngineBackend.getRulesByKeyword(customerId, token, keyword);
          if (!rules || rules.length === 0) {
               setTableData([]);
               return 0;
          }
          const channelFiltered = channelId == null || channelId < 0
               ? rules
               : rules.filter((row) => row.channel && Number(row.channel.id) === Number(channelId));
          setTableData(mapRulesForTable(sortRules(channelFiltered)));
          return channelFiltered.length;
     }

     function runKeywordSearch() {
          const keyword = searchKeyword.trim();
          if (!keyword) {
               clearKeywordSearch();
               return;
          }
          pendingSearchToast.current = true;
          setActiveSearchKeyword(keyword);
     }

     function clearKeywordSearch() {
          setSearchKeyword("");
          setActiveSearchKeyword("");
     }

     function showEnabledRulesOnly() {
          setActiveSearchKeyword("");
          setSearchKeyword("");
          setFilters((prev) => ({
               ...prev,
               enabled: { value: true, matchMode: FilterMatchMode.EQUALS },
          }));
     }

     useEffect(() => {
          async function fetchData() {
               if (customerId == null) {
                    setIsLoading(false);
                    return;
               }
               setIsLoading(true);
               try {
                    const isStagingActive = await RuleEngineBackend.getIsStagingActive(token);
                    setStagingModeActive(isStagingActive);

                    const allChannels = await RuleEngineBackend.getChannels(customerId, token);
                    let channelId = selectedChannelID;
                    const selected = channelsForSelect(allChannels);
                    if (selected) {
                         setChannels(selected.visible);
                         const resolvedChannelId = resolveChannelId(selected.channelList, customerId, channelId);
                         if (resolvedChannelId !== channelId) {
                              channelId = resolvedChannelId;
                              setSelectedChannelID(channelId);
                         }
                    }

                    if (channelId == null) {
                         setTableData([]);
                         return;
                    }

                    if (activeSearchKeyword) {
                         const count = await loadKeywordSearchRules(activeSearchKeyword, channelId);
                         if (pendingSearchToast.current) {
                              if (count === 0) {
                                   toast.warning("Keine Regeln gefunden!");
                              }
                              pendingSearchToast.current = false;
                         }
                    } else {
                         await loadOverviewRules(channelId);
                    }
               } finally {
                    setIsLoading(false);
               }
          }
          fetchData();
     }, [customerId, userId, token, t, selectedChannelID, activeSearchKeyword]);

     const onFilterChange = (e, field) => {
          const value = e.target.value;
          const matchMode = field === "enabled" ? FilterMatchMode.EQUALS : FilterMatchMode.CONTAINS;
          const updatedFilters = { ...filters, [field]: { value, matchMode } };
          setFilters(updatedFilters);
     };

     const onEnabledFilterChange = (e) => {
          const raw = e.target.value;
          let value = null;
          if (raw === "true") value = true;
          if (raw === "false") value = false;
          setFilters((prev) => ({
               ...prev,
               enabled: { value, matchMode: FilterMatchMode.EQUALS },
          }));
     };

     const onDateFilterChange = (field, value) => {
          setFilters((prev) => ({
               ...prev,
               [field]: { value: value ?? null, matchMode: FilterMatchMode.DATE_IS },
          }));
     };

     const onFilter = (e) => {
          setFilters(e.filters);
     };

     const renderColumnFilter = (field) => {
          if (field === "enabled") {
               const v = filters.enabled?.value;
               const selectVal = v === true ? "true" : v === false ? "false" : "";
               return (
                    <select
                         value={selectVal}
                         onChange={onEnabledFilterChange}
                         className="form-select form-select-sm"
                         style={{ width: "100%", fontSize: "12px" }}>
                         <option value="">Alle</option>
                         <option value="true">Ja</option>
                         <option value="false">Nein</option>
                    </select>
               );
          }
          if (isDateFilterField(field)) {
               const { locale, dateFormat } = getPrimeCalendarConfig(i18n.language);
               return (
                    <PPColumnDateFilter
                         value={filters[field]?.value}
                         onChange={(value) => onDateFilterChange(field, value)}
                         placeholder={filterTranslationMap[field]}
                         locale={locale}
                         dateFormat={dateFormat}
                    />
               );
          }
          return (
               <InputText
                    value={filters[field]?.value ?? ""}
                    onChange={(e) => onFilterChange(e, field)}
                    placeholder={filterTranslationMap[field]}
                    className="p-inputtext p-column-filter"
                    style={{
                         width: "100%",
                         fontSize: "12px",
                         padding: "6px 8px",
                         border: "1px solid #ced4da",
                         borderRadius: "4px",
                    }}
               />
          );
     };

     const deleteButtonTemplate = (rowData) => {
          return (
               <button
                    onClick={() => {
                         setRulePendingDelete(rowData);
                         setRuleDeleteModalVisible(true);
                    }}
                    type="button"
                    className="btn btn-outline-danger w-100"
                    disabled={isLoading}
               >
                    <i className="nav-icon fas fa-trash p-1"></i>
               </button>
          );
     };

     async function confirmDeleteRule() {
          if (!rulePendingDelete) {
               return;
          }

          setIsLoading(true);
          const response = await RuleEngineBackend.deleteRule(userId, token, rulePendingDelete.id);
          if (response?.ok) {
               setTableData((prev) => prev.filter((row) => row.id !== rulePendingDelete.id));
               toast.success("Regel erfolgreich gelöscht");
          } else {
               toast.error(t("pricemanagement_page.overview_page.toast_error"));
          }
          setIsLoading(false);
          setRuleDeleteModalVisible(false);
          setRulePendingDelete(null);
     }

     const triggerButtonTemplate = (rowData) => {
          return (
               <button
                    onClick={async () => {
                         setIsLoading(true);
                         try {
                              await RuleEngineBackend.triggerRule(token, rowData.id);
                              toast.success(t("pricemanagement_page.overview_page.toast_rule_triggered"));
                         } catch (error) {
                              toast.error(error?.message || t("pricemanagement_page.overview_page.toast_error"));
                         } finally {
                              setIsLoading(false);
                         }
                    }}
                    type="button"
                    className="btn btn-outline-primary w-100"
                    disabled={isLoading}
               >
                    {t("pricemanagement_page.overview_page.action_trigger_rule")}
               </button>
          );
     };

     const enabledStatusTemplate = (rowData) => {
          const isEnabled = Boolean(rowData.enabled);
          const switchId = `rule-enabled-${rowData.id}`;
          return (
               <div
                    className="form-check form-switch d-flex justify-content-center m-0 pp-rule-enabled-toggle"
               >
                    <input
                         id={switchId}
                         className="form-check-input"
                         type="checkbox"
                         role="switch"
                         checked={isEnabled}
                         disabled={isLoading}
                         title={isEnabled ? "Regel deaktivieren" : "Regel aktivieren"}
                         onClick={(e) => e.stopPropagation()}
                         onChange={async (e) => {
                              e.stopPropagation();
                              const nextEnabled = e.target.checked;
                              setIsLoading(true);
                              try {
                                   if (nextEnabled) {
                                        await RuleEngineBackend.activateRule(userId, token, rowData.id);
                                   } else {
                                        await RuleEngineBackend.deactivateRule(userId, token, rowData.id);
                                   }
                                   setTableData((prev) =>
                                        prev.map((row) =>
                                             row.id === rowData.id ? { ...row, enabled: nextEnabled } : row
                                        )
                                   );
                                   toast.success(nextEnabled ? "Regel aktiviert" : "Regel deaktiviert");
                              } catch (err) {
                                   toast.error(t("pricemanagement_page.overview_page.toast_error"));
                              }
                              setIsLoading(false);
                         }}
                    />
               </div>
          );
     };

     const createDateTemplate = (rowData) => PPUtils.formatDateDE(rowData.createDate, true);

     const columns = [
          { field: 'id', header: t("pricemanagement_page.overview_page.table_header_id"), filterable: true },
          { field: 'rulePriority', header: t("pricemanagement_page.overview_page.table_header_priority"), filterable: true },
          { field: 'enabled', header: t("pricemanagement_page.overview_page.table_header_active"), filterable: true, body: enabledStatusTemplate },
          { field: 'name', header: t("pricemanagement_page.overview_page.table_header_name"), filterable: true },
          { field: 'description', header: t("pricemanagement_page.overview_page.table_header_description"), filterable: true },
          { field: 'createDateFormatted', header: t("pricemanagement_page.overview_page.table_header_creation_date"), filterable: true, body: createDateTemplate, sortField: 'createDate' },
          { field: 'ruleGroup.groupName', header: t("pricemanagement_page.overview_page.table_header_group"), filterable: true },
          { field: 'creatorUser.name', header: t("pricemanagement_page.overview_page.table_header_author"), filterable: true },
          { field: 'lastEditUser.name', header: t("pricemanagement_page.overview_page.table_header_last_edit"), filterable: true },
          { field: 'trigger', header: t("pricemanagement_page.overview_page.table_header_trigger_rule"), filterable: false, body: triggerButtonTemplate },
          { field: 'del', header: t("pricemanagement_page.overview_page.table_header_delete_rule"), filterable: false, body: deleteButtonTemplate }
     ];

     if (customerId == null) {
          return (
               <div className="container-fluid priceman-page">
                    <div className="card shadow">
                         <div className="card-body">
                              <PPMatchingStatusBanner variant="no_customer" />
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               {isLoading && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                         style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                         <div className="text-center text-white">
                              <output className="spinner-border mb-3">
                                   <span className="visually-hidden">Loading...</span>
                              </output>
                              <div>Verarbeitung läuft...</div>
                         </div>
                    </div>
               )}
               <div className="container-fluid priceman-page">
                    <div className="row">
                         <div className="col-12">
                              <div className="card shadow">
                                   <div className="card-body">
                                        <div className='p-3 d-flex flex-column priceman-page-body'>
                                             <div className="priceman-rules-toolbar">
                                                  <div className="priceman-rules-toolbar__group">
                                                       <Select
                                                                 className="priceman-channel-select"
                                                                 isClearable={false}
                                                                 value={
                                                                      selectedChannelID != null
                                                                           ? channels
                                                                                .map((channel) => ({
                                                                                     label: channel.channelName,
                                                                                     id: channel.id,
                                                                                     value: channel.id,
                                                                                }))
                                                                                .find((o) => o.id === selectedChannelID) || null
                                                                           : null
                                                                 }
                                                                 onChange={(event) => selectChannel(customerId, event ? event.id : null, setSelectedChannelID)}
                                                                 options={channels.map((channel) => ({
                                                                      label: channel.channelName,
                                                                      id: channel.id,
                                                                      value: channel.id,
                                                                 }))}
                                                       />
                                                       <div className="priceman-rules-search">
                                                            <InputText
                                                                 value={searchKeyword}
                                                                 onChange={(e) => {
                                                                      const value = e.target.value;
                                                                      setSearchKeyword(value);
                                                                      if (!value.trim() && activeSearchKeyword) {
                                                                           clearKeywordSearch();
                                                                      }
                                                                 }}
                                                                 onKeyDown={(e) => {
                                                                      if (e.key === "Enter") {
                                                                           e.preventDefault();
                                                                           runKeywordSearch();
                                                                      }
                                                                 }}
                                                                 placeholder={t("pricemanagement_page.overview_page.rule_search_placeholder")}
                                                            />
                                                            <button type="button" className="btn btn-outline-primary" onClick={runKeywordSearch}>
                                                                 Suchen
                                                            </button>
                                                            {activeSearchKeyword && (
                                                                 <button type="button" className="btn btn-outline-secondary" onClick={clearKeywordSearch}>
                                                                      Zurücksetzen
                                                                 </button>
                                                            )}
                                                            <i
                                                                 className="fas fa-info-circle text-muted flex-shrink-0"
                                                                 style={{ fontSize: "1.1rem", cursor: "help" }}
                                                                 title={t("pricemanagement_page.overview_page.rule_search_info")}
                                                            />
                                                       </div>
                                                       <button type="button" className="btn btn-outline-secondary" onClick={showEnabledRulesOnly}>
                                                            Aktive anzeigen
                                                       </button>
                                                       <button onClick={() => setReloadDialogOpen(true)} type="button" className="btn btn-outline-primary">{t("pricemanagement_page.overview_page.reload_all_prices")}</button>
                                                       <button onClick={() => setDeleteDialogOpen(true)} type="button" className="btn btn-outline-primary">{t("pricemanagement_page.overview_page.delete_all_prices")}</button>
                                                  </div>

                                                  <div className="priceman-rules-toolbar__group">
                                                       <button onClick={resetAllFilters} type="button" className="btn btn-outline-primary">{t("pricemanagement_page.overview_page.action_reset_filters")}</button>
                                                       <div className="d-flex align-items-center">
                                                            {
                                                                 stagingModeActive ?
                                                                      (<button onClick={() => setStagingDialogOpen(true)} type="button" className="btn btn-success">{t("pricemanagement_page.overview_page.staging_mode") + ": ON"}</button>)
                                                                      :
                                                                      (<button onClick={activateStagingMode} type="button" className="btn btn-danger">{t("pricemanagement_page.overview_page.staging_mode") + ": OFF"}</button>)
                                                            }
                                                            <i
                                                                 className="fas fa-info-circle ms-2 text-muted"
                                                                 style={{ fontSize: "1.1rem" }}
                                                                 title="Staging-Modus ist ein Testmodus. Änderungen werden nicht live gestellt."
                                                            />
                                                       </div>
                                                  </div>
                                             </div>
                                             <div className="d-flex flex-row align-items-center mt-3">
                                                  <div className="col-md-auto d-flex align-items-center">
                                                       <p className="mb-0">
                                                            {activeSearchKeyword
                                                                 ? `Suchergebnisse für "${activeSearchKeyword}": ${tableData.length}`
                                                                 : t("pricemanagement_page.overview_page.defined_rules") + ": " + tableData.length}
                                                       </p>
                                                  </div>
                                                  <button onClick={() => navigate("/pricemanagement/editor")} type="button" className="btn btn-primary ms-3">{t("pricemanagement_page.overview_page.action_add_rule")}</button>
                                             </div>
                                             <div className="d-flex flex-row mt-2 priceman-table-row">
                                                  <div className="col-12 priceman">
                                                       <div className="priceman-table-wrap priceman-table-wrap--paged" ref={tableWrapRef}>
                                                            <DataTable
                                                                 size='small'
                                                                 stripedRows
                                                                 removableSort
                                                                 value={tableData}
                                                                 globalFilterFields={['id', 'rulePriority', 'enabled', 'name', 'description', 'createDateFormatted', 'ruleGroup.groupName', 'creatorUser.name', 'lastEditUser.name']}
                                                                 filters={filters}
                                                                 onFilter={onFilter}
                                                                 paginator
                                                                 rows={pageSize}
                                                                 filterDisplay="row"
                                                                 sortMode="multiple"
                                                                 multiSortMeta={multiSortMeta}
                                                                 onSort={(e) => setMultiSortMeta(e.multiSortMeta || [])}
                                                                 onRowClick={openRuleEdit}
                                                                 emptyMessage={t("pricetable.datatable.nodata")}
                                                                 rowClassName={() => 'cursor-pointer'}
                                                                 selectionMode="single"
                                                                 scrollable
                                                                 scrollHeight="flex"
                                                            >
                                                                 {columns.map((col, i) => (
                                                                      <Column
                                                                           key={col.field + "_rules_" + i}
                                                                           field={col.field}
                                                                           filterField={col.field === "createDateFormatted" ? "createDate" : undefined}
                                                                           dataType={col.field === "createDateFormatted" ? "date" : undefined}
                                                                           header={col.header}
                                                                           headerStyle={{
                                                                                border: "1px solid #ddd",
                                                                                backgroundColor: "#f8f9fa",
                                                                                fontWeight: "600",
                                                                                fontSize: "14px",
                                                                                padding: "12px 8px",
                                                                                textAlign: "center",
                                                                                color: "#495057",
                                                                                verticalAlign: "middle"
                                                                           }}
                                                                           filterHeaderStyle={{
                                                                                border: "1px solid #ddd",
                                                                                backgroundColor: "#ffffff",
                                                                                padding: "8px"
                                                                           }}
                                                                           body={col.body}
                                                                           sortable={true}
                                                                           showFilterMenu={false}
                                                                           filter={col.filterable}
                                                                           filterMatchModeOptions={[{ label: 'contains', value: FilterMatchMode.CONTAINS }]}
                                                                           filterElement={renderColumnFilter(col.field)}
                                                                      />
                                                                 ))}
                                                            </DataTable>
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               {/* delete prices modal */}
               <Dialog
                    header={t("pricemanagement_page.overview_page.delete_all_prices")}
                    footer={
                         <>
                              <button className="btn btn-danger btn-lg m-2" onClick={() => { setDeleteDialogOpen(false) }}>
                                   {t("single.cancel")}
                              </button>
                              <button className="btn btn-primary btn-lg m-2" onClick={() => { deleteAllPrices(); setDeleteDialogOpen(false) }}>
                                   {t("single.ok")}
                              </button>
                         </>
                    }
                    visible={deleteDialogOpen}
                    style={{ width: '30vw' }}
                    onHide={() => { if (!deleteDialogOpen) return; setDeleteDialogOpen(false); }}
               >
                    <p className="m-0">
                         Alle berechneten Preise werden gelöscht.
                    </p>
               </Dialog>
               {/* reload prices modal */}
               <Dialog
                    header={t("pricemanagement_page.overview_page.reload_all_prices")}
                    footer={
                         <>
                              <button className="btn btn-danger btn-lg m-2" onClick={() => { setReloadDialogOpen(false) }}>
                                   Abbrechen
                              </button>
                              <button className="btn btn-primary btn-lg m-2" onClick={() => { reloadAllPrices(); setReloadDialogOpen(false) }}>
                                   OK
                              </button>
                         </>
                    }
                    visible={reloadDialogOpen}
                    style={{ width: '30vw' }}
                    onHide={() => { if (!reloadDialogOpen) return; setReloadDialogOpen(false); }}
               >
                    <p className="m-0">
                         Alle berechneten Preise werden neu berechnet.
                    </p>
               </Dialog>
               {/* staging modal */}
               <Dialog
                    header={t("pricemanagement_page.overview_page.modal_staging_deactivate")}
                    footer={
                         <>
                              <button className="btn btn-danger btn-lg m-2" onClick={() => { setStagingDialogOpen(false) }}>
                                   Abbrechen
                              </button>
                              <button className="btn btn-primary btn-lg m-2" onClick={() => { deactivateStagingMode(); setStagingDialogOpen(false) }}>
                                   OK
                              </button>
                         </>
                    }
                    visible={stagingDialogOpen}
                    style={{ width: '30vw' }}
                    onHide={() => { if (!stagingDialogOpen) return; setStagingDialogOpen(false); }}
               >
                    <p className="m-0">
                         {t("pricemanagement_page.overview_page.modal_staging_will_be_deactivated")}
                    </p>
               </Dialog>

               <PPDeleteConfirmationModal
                    visible={ruleDeleteModalVisible}
                    onClose={() => {
                         setRuleDeleteModalVisible(false);
                         setRulePendingDelete(null);
                    }}
                    onConfirm={confirmDeleteRule}
                    message={
                         rulePendingDelete
                              ? `Sind Sie sicher, dass Sie die Regel "${rulePendingDelete.name || `ID ${rulePendingDelete.id}`}" löschen möchten?`
                              : "Sind Sie sicher, dass Sie diese Regel löschen möchten?"
                    }
               />
          </>
     );
}